import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CheckCircle2, XCircle, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../../config/api';

interface ReprocessingStep {
  id: string;
  name: string;
  description: string;
}

const REPROCESSING_STEPS: ReprocessingStep[] = [
  { id: 'html', name: 'HTML Extraction', description: 'Fetching and storing page HTML' },
  { id: 'text', name: 'Text Extraction', description: 'Extracting text content from HTML' },
  { id: 'semantic', name: 'Semantic Analysis', description: 'Analyzing content structure and meaning' },
  { id: 'embeddings', name: 'Embeddings Generation', description: 'Creating vector embeddings for search' },
  { id: 'images', name: 'Image Extraction', description: 'Finding and cataloging page images' },
];

interface PageProgress {
  pageId: string;
  url: string;
  title: string;
  steps: {
    [stepId: string]: 'pending' | 'processing' | 'completed' | 'failed';
  };
  error?: string;
}

interface ReprocessingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  selectedPageIds: string[];
  totalPages: number;
  onComplete?: () => void;
}

export function ReprocessingDialog({
  open,
  onOpenChange,
  siteId,
  selectedPageIds,
  totalPages,
  onComplete
}: ReprocessingDialogProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [pageProgress, setPageProgress] = useState<Record<string, PageProgress>>({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !siteId) return;

    // Connect to Socket.IO
    // For localhost, always connect to backend on port 3001
    const socketUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : (SOCKET_URL || window.location.origin);
    console.log('[ReprocessingDialog] Connecting to Socket.IO at:', socketUrl);
    const newSocket = io(socketUrl, {
      path: '/socket.io/',
      transports: ['polling', 'websocket'], // Start with polling for stability
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      upgrade: true, // Allow upgrading from polling to websocket
    });

    newSocket.on('connect', () => {
      console.log('[ReprocessingDialog] Socket connected');
      setError(null); // Clear any connection errors
      // Join the reprocessing room for this site
      const room = `reprocessing:${siteId}`;
      console.log('[ReprocessingDialog] Joining room:', room);
      newSocket.emit('join-reprocessing', { siteId, pageIds: selectedPageIds });
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[ReprocessingDialog] Socket disconnected:', reason);
      // Don't show errors for intentional disconnects
      if (reason === 'io client disconnect' || !open) {
        return;
      }
      // Socket.IO will handle reconnection automatically for other cases
    });

    newSocket.on('connect_error', (error) => {
      console.error('[ReprocessingDialog] Socket connection error:', error.message);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('[ReprocessingDialog] Socket reconnected after', attemptNumber, 'attempts');
      setError(null); // Clear error on successful reconnect
      // Rejoin room after reconnection
      newSocket.emit('join-reprocessing', { siteId, pageIds: selectedPageIds });
    });

    // Listen for reprocessing start event
    newSocket.on(`reprocessing:${siteId}:start`, (data: any) => {
      console.log('[ReprocessingDialog] Reprocessing started:', data);
      // Initialize progress for all pages
      const initialProgress: Record<string, PageProgress> = {};
      data.pages.forEach((page: any) => {
        initialProgress[page.id] = {
          pageId: page.id,
          url: page.url,
          title: page.title || 'Untitled',
          steps: REPROCESSING_STEPS.reduce((acc, step) => {
            acc[step.id] = 'pending';
            return acc;
          }, {} as Record<string, 'pending' | 'processing' | 'completed' | 'failed'>)
        };
      });
      setPageProgress(initialProgress);
    });

    // Listen for progress updates
    newSocket.on(`reprocessing:${siteId}:progress`, (data: any) => {
      console.log('[ReprocessingDialog] Progress update:', data);
      setPageProgress(prev => ({
        ...prev,
        [data.pageId]: {
          ...prev[data.pageId],
          steps: {
            ...prev[data.pageId]?.steps,
            [data.stepId]: data.status
          },
          error: data.error
        }
      }));
    });

    // Listen for completion
    newSocket.on(`reprocessing:${siteId}:complete`, () => {
      console.log('[ReprocessingDialog] Reprocessing complete');
      setIsComplete(true);
      if (onComplete) {
        onComplete();
      }
    });

    // Listen for errors
    newSocket.on(`reprocessing:${siteId}:error`, (data: any) => {
      console.log('[ReprocessingDialog] Reprocessing error:', data);
      setError(data.message || 'An error occurred during reprocessing');
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        console.log('[ReprocessingDialog] Cleaning up socket');
        newSocket.emit('leave-reprocessing', { siteId });
        newSocket.removeAllListeners();
        newSocket.close();
      }
    };
  }, [open, siteId, selectedPageIds, onComplete]);

  // Calculate overall progress
  useEffect(() => {
    const allSteps = Object.values(pageProgress).flatMap(page => 
      Object.values(page.steps)
    );
    const completedSteps = allSteps.filter(status => 
      status === 'completed' || status === 'failed'
    ).length;
    const progress = allSteps.length > 0 
      ? Math.round((completedSteps / allSteps.length) * 100)
      : 0;
    setOverallProgress(progress);

    // Check if all steps are complete
    if (progress === 100 && allSteps.length > 0) {
      setIsComplete(true);
    }
  }, [pageProgress]);

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStepBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      processing: 'secondary',
      failed: 'destructive',
      pending: 'outline'
    };
    return <Badge variant={variants[status] || 'outline'} className="text-xs">{status}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Reprocessing Pages
          </DialogTitle>
          <DialogDescription>
            Processing {totalPages} pages with selected options
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Overall Progress */}
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Page Progress List */}
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {Object.values(pageProgress).map((page) => (
                <div key={page.pageId} className="border rounded-lg p-4 space-y-3">
                  <div>
                    <h4 className="font-medium text-sm">{page.title}</h4>
                    <p className="text-xs text-muted-foreground truncate">{page.url}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {REPROCESSING_STEPS.map((step) => {
                      const status = page.steps[step.id] || 'pending';
                      return (
                        <div key={step.id} className="flex items-center gap-2">
                          {getStepIcon(status)}
                          <div className="flex-1">
                            <p className="text-xs font-medium">{step.name}</p>
                            <p className="text-xs text-muted-foreground">{step.description}</p>
                          </div>
                          {getStepBadge(status)}
                        </div>
                      );
                    })}
                  </div>

                  {page.error && (
                    <div className="text-xs text-red-600 mt-2">
                      Error: {page.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={!isComplete && overallProgress > 0 && overallProgress < 100}
            >
              {isComplete ? 'Close' : 'Cancel'}
            </Button>
            {isComplete && onComplete && (
              <Button onClick={onComplete}>
                Refresh Page List
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}